/**
 * Bridge React Context Provider
 *
 * Wraps a BridgeClient singleton in React context with auto-connect
 * on mount and auto-disconnect on unmount. Provides connection status
 * as reactive state for consuming components.
 *
 * The bridge talks to the UE plugin's local sidecar on `/ws/bridge`, derived
 * from the sidecar base the C++ shell injects via `?apiBase=` (so the WS port
 * always matches the REST port — see `bridgeWsUrl`). It is ONLY relevant in the
 * plugin's WebView2 host — i.e. when the SPA is loaded under an `/embed/*`
 * route. In a normal browser session on app.gamedevbuddy.com (marketing,
 * console, etc.) there is no sidecar to connect to, so the provider must NOT
 * attempt a connection — otherwise every visitor's devtools fills with
 * "connection refused" reconnect-loop spam.
 *
 * Default behaviour: connect only when `window.location.pathname` starts
 * with `/embed/`. Override via the `enabled` prop for tests or
 * special-case mounts.
 *
 * Usage:
 *   <BridgeProvider>
 *     <App />
 *   </BridgeProvider>
 *
 * Or force-on for tests / specific mounts:
 *   <BridgeProvider enabled={true}>
 *     <App />
 *   </BridgeProvider>
 */

import { createContext, useEffect, useState, type ReactNode } from 'react'
import { bridgeWsUrl } from '@/lib/api/apiUrl'
import { BridgeClient } from './bridgeClient'
import { bridgeStateCache } from './bridgeStateCache'

export interface BridgeContextValue {
  client: BridgeClient | null
  isConnected: boolean
}

export const BridgeContext = createContext<BridgeContextValue>({
  client: null,
  isConnected: false,
})

interface BridgeProviderProps {
  url?: string
  /**
   * Force-enable or force-disable the bridge connection. When undefined
   * (the default), the provider only connects if the current URL is an
   * `/embed/*` route — i.e. we're inside the UE plugin's WebView2.
   */
  enabled?: boolean
  children: ReactNode
}

/**
 * Pure helper — exported so unit tests can verify the path-based default
 * without spinning up a window mock.
 */
export function shouldEnableBridge(pathname: string | undefined): boolean {
  return typeof pathname === 'string' && pathname.startsWith('/embed/')
}

export function BridgeProvider({
  url,
  enabled,
  children,
}: BridgeProviderProps) {
  const [client, setClient] = useState<BridgeClient | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  // Resolve the gate at mount: explicit prop wins, otherwise pathname.
  // Pathname is read once at mount — embed routes load in their own
  // WebView and never navigate to non-embed URLs in-place, so a single
  // read at mount captures the right context.
  const isActive =
    enabled ??
    (typeof window !== 'undefined' &&
      shouldEnableBridge(window.location.pathname))

  useEffect(() => {
    if (!isActive) {
      return
    }
    const c = new BridgeClient(url ?? bridgeWsUrl())
    c.onStatusChange = (connected) => setIsConnected(connected)
    c.connect()
    setClient(c)
    // Attach to the module-singleton bridge state cache so `useBridgeState`
    // hooks can send state.* commands through this client. The cache lives
    // outside React and survives remounts — attach on every mount so a
    // reconnect or HMR swap points it at the fresh client instance.
    bridgeStateCache.attachClient(c)

    return () => {
      c.disconnect()
      setClient(null)
    }
  }, [url, isActive])

  return (
    <BridgeContext.Provider value={{ client, isConnected }}>
      {children}
    </BridgeContext.Provider>
  )
}
